import React, { useEffect, useState } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import { fetchReportingDetails } from "../../../../api/ApiConfig"; 
import "./OrgTreeModal.css";

const OrgTreeModal = ({ empId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [rootNode, setRootNode] = useState(null);

  // 1. DATA TRANSFORMATION LOGIC
  // This function turns the flat API lists into a nested Tree Object
  const buildHierarchy = (apiData) => {
    if (!apiData) return null;

    const { employee, direct_reportees, reports_to } = apiData;

    // A. The "Focus" Node (The employee you clicked on)
    // We attach the direct reportees to this node.
    const focusNode = {
      ...employee,
      children: direct_reportees || [], // These will be the grid items
      nodeType: "focus", // Tag for Green Styling
    };

    // B. If there are no managers above, the Focus Node is the Root.
    if (!reports_to || reports_to.length === 0) {
      return focusNode;
    }

    // C. If there ARE managers, we must build the chain downwards.
    // API usually gives reports_to as: [DirectManager, GrandBoss, CEO] (Ascending)
    // OR [CEO, GrandBoss, DirectManager] (Descending).
    // We assume the standard: reports_to[0] is Direct Manager.
    // We need to reverse it to start from the Top (CEO) down to Manager.
    
    // Let's handle it safely:
    // We want the structure: Ancestor 1 -> Ancestor 2 -> ... -> FocusNode
    
    const ancestors = [...reports_to].reverse(); // Now: [CEO, VP, Manager]

    // Start the chain with the Top-most person
    let root = { 
        ...ancestors[0], 
        nodeType: "ancestor",
        children: [] 
    };
    
    let currentPointer = root;

    // Loop through the rest of the ancestors and link them
    for (let i = 1; i < ancestors.length; i++) {
      const nextNode = { 
          ...ancestors[i], 
          nodeType: "ancestor",
          children: [] 
      };
      currentPointer.children = [nextNode]; // Link down
      currentPointer = nextNode; // Move pointer
    }

    // Finally, attach the Focus Node to the last ancestor
    currentPointer.children = [focusNode];

    return root;
  };

  // 2. FETCH EFFECT
  useEffect(() => {
    if (!empId) return;
    setLoading(true);

    fetchReportingDetails({ emp_id: empId })
      .then((res) => {
        if (res?.data?.length) {
            const tree = buildHierarchy(res.data[0]);
            setRootNode(tree);
        }
      })
      .catch((err) => console.error("Hierarchy Fetch Error:", err))
      .finally(() => setLoading(false));
  }, [empId]);

  // 3. HELPER: Initials
  const getInitials = (name) => {
    if (!name) return "NA";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // 4. COMPONENT: Card
  const EmployeeCard = ({ data, type }) => {
    // Determine styling class
    let cardClass = "org-node-card";
    if (type === "focus") cardClass += " is-focus";
    if (type === "ancestor") cardClass += " is-ancestor";

    return (
      <div className={cardClass}>
        <div className="card-avatar">
          {data.profile_photo ? (
            <img src={data.profile_photo} alt={data.name} />
          ) : (
            getInitials(data.name)
          )}
        </div>
        <div className="card-info">
          <div className="card-name" title={data.name}>{data.name}</div>
          <div className="card-role">{data.designation || "N/A"}</div>
          <div className="card-dept">{data.department}</div>
          <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>ID: {data.employee_id}</div>
        </div>
      </div>
    );
  };

  // 5. RECURSIVE RENDERER
  const renderTreeNodes = (node) => {
    if (!node) return null;

    // Check if this is the Focus Node (The one with the Reportees Grid)
    if (node.nodeType === "focus") {
        // If this node has children, they are the reportees -> RENDER GRID
        const hasReportees = node.children && node.children.length > 0;
        
        return (
            <TreeNode label={<EmployeeCard data={node} type="focus" />}>
                {hasReportees && (
                    <TreeNode 
                        label={
                            <div className="grid-group-container">
                                {node.children.map(child => (
                                    <EmployeeCard 
                                        key={child.employee_id} 
                                        data={child} 
                                        type="child" 
                                    />
                                ))}
                            </div>
                        } 
                    />
                )}
            </TreeNode>
        );
    }

    // Otherwise, it's an Ancestor. Render normally and recurse.
    return (
        <TreeNode label={<EmployeeCard data={node} type="ancestor" />}>
            {node.children && node.children.map(child => (
                // Recurse down
                <React.Fragment key={child.employee_id}>
                    {renderTreeNodes(child)}
                </React.Fragment>
            ))}
        </TreeNode>
    );
  };

  return (
    <div className="org-modal-overlay" onClick={onClose}>
      <div className="org-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="org-modal-header">
          <h2 className="org-title">Reporting Structure</h2>
          <button className="org-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="org-modal-body">
          {loading && <div style={{ margin: 'auto', color: '#666' }}>Loading Hierarchy...</div>}
          
          {!loading && rootNode && (
            <div style={{ paddingBottom: '40px' }}>
                <Tree
                    lineWidth={"2px"}
                    lineColor={"#6a67f7"}
                    lineStyle={"dashed"}
                    label={
                        /* Root Label is rendered inside renderTreeNodes logic if we pass rootNode as child, 
                           but 'Tree' expects a root label prop. 
                           So we handle the Root visualization manually here. 
                        */
                        <EmployeeCard data={rootNode} type={rootNode.nodeType} />
                    }
                >
                   {/* Render the children of the Root */}
                   {rootNode.children && rootNode.children.map(child => renderTreeNodes(child))}
                </Tree>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgTreeModal;